import type { JSX } from 'react';
import type { RequestDetailResource } from '@majuscule/contracts';
import { requestInputSchema, smsResultSchema } from '@majuscule/contracts';
import { assertNever } from '@majuscule/domain';
import { useQuery } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { z } from 'zod';

import { EnvironmentBadge } from '@/components/EnvironmentBadge';
import { RequestDocuments, requestDocumentCount } from '@/components/RequestDocuments';
import {
  formatInt,
  formatLongDate,
  formatTimeSeconds,
  parisDayKey,
  shortRequestId,
} from '@/components/requests/request-format';
import { PageChrome } from '@/components/page-chrome';
import { Callout } from '@/components/ui/alert';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Card, CardBar, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { ChartLegend, ProportionBar } from '@/components/ui/chart';
import { CodeBlock, CopyButton, Details } from '@/components/ui/code';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { Kv, KvItem, KvList, KvRow } from '@/components/ui/kv';
import { Row4, Split21, Stack } from '@/components/ui/layout-grid';
import { PageHead } from '@/components/ui/page-header';
import { Panel } from '@/components/ui/panel';
import { StatCard } from '@/components/ui/stat-card';
import { outcomeLabel, requestStatusLabel, StatusBadge } from '@/components/ui/status-tag';
import { Tag } from '@/components/ui/tag';
import { Tabs, TabCount, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Timeline } from '@/components/ui/timeline';
import { ApiHttpError, internalFetch } from '@/lib/api';
import { formatEur, formatEuros, formatPercent, microToDecimal, toMicro } from '@/lib/format';
import { ENCODING_LABELS } from '@/lib/use-cases';

type Attempt = RequestDetailResource['attempts'][number];
type DetailTab = 'input' | 'attempts' | 'result' | 'documents';

const ATTEMPT_STATUS_LABELS: Record<Attempt['status'], string> = {
  sent: 'Envoyée',
  completed: 'Terminée',
  abandoned: 'Abandonnée',
};

const MODE_LABELS: Record<Attempt['mode'], string> = {
  standard: 'Standard',
  batch: 'Lot (batch)',
};

/** Result payloads we can render specially; anything else falls back to JSON. */
const textResultSchema = z.object({ kind: z.literal('text'), text: z.string() });
const imageResultSchema = z.object({ kind: z.literal('image'), fileIds: z.array(z.string()) });
const extractionResultSchema = z.object({
  kind: z.literal('document_extraction'),
  data: z.unknown(),
  warnings: z.array(z.string()).optional(),
});

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  return new Date(iso).toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris',
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}

/** Elapsed time between two timestamps: "3 s", "2 min 5 s", "1 h 4 min". */
function formatDuration(fromIso: string, toIso: string): string {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) {
    return '—';
  }
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}\u00a0s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds % 60;
    return rest > 0 ? `${minutes}\u00a0min ${rest}\u00a0s` : `${minutes}\u00a0min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours}\u00a0h ${rest}\u00a0min` : `${hours}\u00a0h`;
}

/**
 * Label of the SMS segments counted at submission. Whether they are billed
 * depends on the outcome, which a request still waiting or running (queued,
 * paused, running) does not have yet.
 */
function smsUnitsLabel(outcome: RequestDetailResource['outcome']): string {
  if (outcome === null) {
    return 'Segments';
  }
  return outcome.billable ? 'Segments facturés' : 'Segments (non facturés)';
}

/** Badge of an attempt: its outcome when known, otherwise its transport status. */
function attemptBadge(attempt: Attempt): { variant: BadgeVariant; label: string } {
  if (attempt.outcome !== null) {
    const label = outcomeLabel(attempt.outcome);
    if (attempt.outcome === 'succeeded') {
      return { variant: 'success', label };
    }
    if (attempt.outcome === 'cancelled' || attempt.outcome === 'expired') {
      return { variant: 'neutral', label };
    }
    return { variant: 'danger', label };
  }
  switch (attempt.status) {
    case 'completed':
      return { variant: 'success', label: ATTEMPT_STATUS_LABELS.completed };
    case 'sent':
      return { variant: 'warning', label: ATTEMPT_STATUS_LABELS.sent };
    case 'abandoned':
      return { variant: 'neutral', label: ATTEMPT_STATUS_LABELS.abandoned };
    default:
      return assertNever(attempt.status);
  }
}

/** A labelled block inside a card body (`field-label` above its content). */
function Section({ label, children }: { label: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <div className="grid gap-2">
      <div className="text-label text-muted-foreground">{label}</div>
      <div className="min-w-0 text-[13px]">{children}</div>
    </div>
  );
}

/** Preformatted text that preserves line breaks and wraps long lines. */
function TextBlock({ text }: { text: string }): JSX.Element {
  return <CodeBlock maxHeight={420}>{text.length > 0 ? text : '(vide)'}</CodeBlock>;
}

/** Pretty-printed JSON with a copy button. */
function JsonBlock({ value }: { value: unknown }): JSX.Element {
  // JSON.stringify returns undefined for undefined or a function, despite its type.
  const serialized: unknown = JSON.stringify(value, null, 2);
  const text = typeof serialized === 'string' ? serialized : 'null';
  return (
    <CodeBlock maxHeight={480} copyText={text}>
      {text}
    </CodeBlock>
  );
}

function PurgeNotice(): JSX.Element {
  return (
    <Callout variant="info">
      Contenu purgé (au-delà de la durée de rétention). Les montants et l'historique restent
      disponibles.
    </Callout>
  );
}

/** Human-readable view of the normalized incoming request. */
function RequestInputView({ input }: { input: unknown }): JSX.Element {
  const parsed = requestInputSchema.safeParse(input);
  if (!parsed.success) {
    return <JsonBlock value={input} />;
  }
  const data = parsed.data;
  // Switch on the kind alone: `assertNever` prints its argument, and an SMS input
  // holds a phone number and a message.
  const { kind } = data;
  switch (kind) {
    case 'text':
      return (
        <div className="grid gap-4">
          {data.system !== undefined ? (
            <Section label="Message système">
              <TextBlock text={data.system.text} />
            </Section>
          ) : null}
          <Section label={`Messages (${data.messages.length})`}>
            <div className="grid gap-3">
              {data.messages.map((message, index) => (
                <div key={index} className="grid gap-2">
                  <Tag className="w-fit">
                    {message.role === 'user' ? 'Utilisateur' : 'Assistant'}
                  </Tag>
                  {message.content.map((part, partIndex) =>
                    part.type === 'text' ? (
                      <TextBlock key={partIndex} text={part.text} />
                    ) : (
                      <div
                        key={partIndex}
                        className="flex items-center gap-1.5 text-[13px] text-muted-foreground"
                      >
                        <Paperclip aria-hidden="true" className="size-4" />
                        Fichier : <span className="font-mono text-xs">{part.fileId}</span>
                      </div>
                    ),
                  )}
                </div>
              ))}
            </div>
          </Section>
          {data.responseFormat?.type === 'json_schema' ? (
            <Section label="Schéma de réponse attendu">
              <JsonBlock value={data.responseFormat.schema} />
            </Section>
          ) : null}
          {data.maxOutputTokens !== undefined ? (
            <Section label="Tokens de sortie max">
              <span className="font-mono">{formatInt(data.maxOutputTokens)}</span>
            </Section>
          ) : null}
        </div>
      );
    case 'image':
      return (
        <div className="grid gap-4">
          <Section label="Prompt">
            <TextBlock text={data.prompt} />
          </Section>
          {data.aspectRatio !== undefined ? (
            <Section label="Format">
              <span className="font-mono">{data.aspectRatio}</span>
            </Section>
          ) : null}
          {data.inputFileIds !== undefined && data.inputFileIds.length > 0 ? (
            <Section label="Fichiers d'entrée">
              <span className="font-mono text-xs">{data.inputFileIds.join(', ')}</span>
            </Section>
          ) : null}
        </div>
      );
    case 'sms':
      return (
        <div className="grid gap-4">
          <Section label="Destinataire">
            <span className="font-mono">{data.to}</span>
          </Section>
          <Section label="Message">
            <TextBlock text={data.text} />
          </Section>
        </div>
      );
    default:
      return assertNever(kind);
  }
}

/** Human-readable view of the normalized result. */
function ResultView({ result }: { result: unknown }): JSX.Element {
  const asText = textResultSchema.safeParse(result);
  if (asText.success) {
    return (
      <div className="grid gap-4">
        <TextBlock text={asText.data.text} />
        <Details title="Détail brut (normalisé)">
          <JsonBlock value={result} />
        </Details>
      </div>
    );
  }
  const asExtraction = extractionResultSchema.safeParse(result);
  if (asExtraction.success) {
    return (
      <div className="grid gap-4">
        {asExtraction.data.warnings !== undefined && asExtraction.data.warnings.length > 0 ? (
          <Callout variant="warning" title="Avertissements.">
            {asExtraction.data.warnings.join(' · ')}
          </Callout>
        ) : null}
        <Section label="Données extraites">
          <JsonBlock value={asExtraction.data.data} />
        </Section>
      </div>
    );
  }
  const asSms = smsResultSchema.safeParse(result);
  if (asSms.success) {
    return (
      <div className="grid gap-4">
        <Kv>
          <KvItem label="ID message Vonage" mono>
            <span className="break-all text-xs">{asSms.data.messageId ?? '—'}</span>
          </KvItem>
          <KvItem label="Segments" mono>
            {formatInt(asSms.data.segments)}
          </KvItem>
          <KvItem label="Encodage">{ENCODING_LABELS[asSms.data.encoding]}</KvItem>
          <KvItem label="Expéditeur">{asSms.data.sender}</KvItem>
        </Kv>
        <p className="m-0 text-caption text-muted-foreground">
          Accepté par Vonage pour acheminement ; la réception n’est pas confirmée (aucun accusé de
          réception suivi).
        </p>
        <Details title="Détail brut (normalisé)">
          <JsonBlock value={result} />
        </Details>
      </div>
    );
  }
  const asImage = imageResultSchema.safeParse(result);
  if (asImage.success) {
    return (
      <Section label="Images générées">
        <span className="font-mono text-xs">{asImage.data.fileIds.join(', ') || '(aucune)'}</span>
      </Section>
    );
  }
  return <JsonBlock value={result} />;
}

function AttemptPanel({
  attempt,
  contentPurged,
}: {
  attempt: Attempt;
  contentPurged: boolean;
}): JSX.Element {
  const usage = attempt.usage;
  const eurTariff = attempt.model?.currency === 'EUR';
  const badge = attemptBadge(attempt);
  // An SMS send counts segments, never tokens (lot 17).
  const tokenUsage = usage !== null && usage.messages === 0 ? usage : null;
  const totalTokens = tokenUsage === null ? 0 : tokenUsage.inputTokens + tokenUsage.outputTokens;
  return (
    <Panel
      title={`Tentative n° ${attempt.number}`}
      aside={<Badge variant={badge.variant}>{badge.label}</Badge>}
    >
      <Kv>
        <KvItem label="Modèle" mono={attempt.model !== null}>
          {attempt.model === null
            ? '—'
            : `${attempt.model.provider}, ${attempt.model.providerModelId}`}
        </KvItem>
        <KvItem label="Mode">{MODE_LABELS[attempt.mode]}</KvItem>
        <KvItem label="Coût" mono>
          {attempt.costUnknown
            ? 'inconnu'
            : attempt.costEur !== null
              ? formatEur(attempt.costEur)
              : '—'}
        </KvItem>
        {/* A tariff in EUR (e.g. Vonage) is never converted: no USD cost to show. */}
        {eurTariff ? null : (
          <KvItem label="Coût en dollars" mono>
            {attempt.costUsd !== null ? `${formatEuros(attempt.costUsd)}\u00a0$` : '—'}
          </KvItem>
        )}
        {usage !== null && usage.messages > 0 ? (
          <KvItem label="SMS (segments)" mono>
            {formatInt(usage.messages)}
          </KvItem>
        ) : null}
        {tokenUsage !== null ? (
          <KvItem label="Cache, écriture et lecture" mono>
            {formatInt(tokenUsage.cacheWriteTokens)} / {formatInt(tokenUsage.cacheReadTokens)}
          </KvItem>
        ) : null}
        {tokenUsage !== null && tokenUsage.outputImages > 0 ? (
          <KvItem label="Images" mono>
            {formatInt(tokenUsage.outputImages)}
          </KvItem>
        ) : null}
        {eurTariff ? (
          <KvItem label="Taux de change">En EUR, sans conversion</KvItem>
        ) : (
          <KvItem label="Taux de change" mono>
            {attempt.exchangeRate !== null ? formatEuros(attempt.exchangeRate, 0) : '—'}
          </KvItem>
        )}
        <KvItem label="Tarif" mono>
          <span className="break-all text-xs">{attempt.tariffId ?? '—'}</span>
        </KvItem>
        <KvItem label="ID de requête fournisseur" mono>
          <span className="break-all text-xs">{attempt.providerRequestId ?? '—'}</span>
        </KvItem>
        <KvItem label="Démarrée" mono>
          {formatDateTime(attempt.startedAt)}
        </KvItem>
        <KvItem label="Terminée" mono>
          {formatDateTime(attempt.finishedAt)}
        </KvItem>
      </Kv>

      {tokenUsage !== null ? (
        <div className="grid gap-2">
          <ChartLegend
            className="justify-between"
            items={[
              {
                label: 'Tokens en entrée',
                color: 'chart-1',
                value: formatInt(tokenUsage.inputTokens),
              },
              {
                label: 'Tokens en sortie',
                color: 'chart-4',
                value: formatInt(tokenUsage.outputTokens),
              },
            ]}
          />
          <ProportionBar
            height={10}
            ariaLabel={`Répartition des tokens : ${formatInt(tokenUsage.inputTokens)} en entrée, ${formatInt(tokenUsage.outputTokens)} en sortie, soit ${formatInt(totalTokens)} au total.`}
            segments={[
              { key: 'input', value: tokenUsage.inputTokens, color: 'chart-1' },
              { key: 'output', value: tokenUsage.outputTokens, color: 'chart-4' },
            ]}
          />
        </div>
      ) : null}

      {attempt.errorDetail !== null ? (
        <Section label="Erreur">
          <TextBlock text={attempt.errorDetail} />
        </Section>
      ) : null}

      {contentPurged ? (
        <PurgeNotice />
      ) : (
        <div>
          <Details title="Envoyé au fournisseur (corps exact)">
            {attempt.providerRequest === null ? (
              <p className="m-0 text-[13px] text-muted-foreground">
                Non capturé pour cette tentative.
              </p>
            ) : (
              <JsonBlock value={attempt.providerRequest} />
            )}
          </Details>
          <Details title="Réponse brute du fournisseur">
            {attempt.providerResponse === null ? (
              <p className="m-0 text-[13px] text-muted-foreground">
                Non capturée pour cette tentative.
              </p>
            ) : (
              <JsonBlock value={attempt.providerResponse} />
            )}
          </Details>
        </div>
      )}
    </Panel>
  );
}

/** Foot of the provider cost indicator: the USD cost and rate of the attempts, when converted. */
function providerCostFoot(data: RequestDetailResource): string {
  const converted = data.attempts.filter(
    (attempt) => attempt.costUsd !== null && attempt.model?.currency !== 'EUR',
  );
  const only = converted.length === 1 ? converted[0] : undefined;
  if (only !== undefined && only.costUsd !== null) {
    return only.exchangeRate !== null
      ? `${formatEuros(only.costUsd)}\u00a0$ au taux de ${formatEuros(only.exchangeRate, 0)}`
      : `${formatEuros(only.costUsd)}\u00a0$`;
  }
  if (data.attempts.some((attempt) => attempt.model?.currency === 'EUR')) {
    return 'Tarif en EUR, sans conversion';
  }
  return 'HT';
}

/** Margin indicator (billed amount minus provider cost), computed in micro-euros. */
function marginStat(data: RequestDetailResource): { value: string; foot: string } {
  const amount = toMicro(data.amount);
  const cost = toMicro(data.providerCost);
  if (amount === null || cost === null) {
    return { value: '—', foot: 'Montants illisibles' };
  }
  const margin = amount - cost;
  const foot =
    amount > 0n
      ? `${formatPercent((Number(margin) / Number(amount)) * 100)} du montant facturé`
      : 'Aucun montant facturé';
  return { value: formatEur(microToDecimal(margin)), foot };
}

/** A timeline time: the time alone when on the creation day, otherwise date and time. */
function timelineTime(iso: string | null, createdAt: string): string {
  if (iso === null) {
    return '—';
  }
  return parisDayKey(iso) === parisDayKey(createdAt) ? formatTimeSeconds(iso) : formatDateTime(iso);
}

export function RequestDetail({ requestId }: { requestId: string }): JSX.Element {
  const [tab, setTab] = useState<DetailTab>('input');
  const query = useQuery({
    queryKey: ['request-detail', requestId],
    queryFn: () => internalFetch<RequestDetailResource>(`/internal/requests/${requestId}`),
    retry: (count, error) => !(error instanceof ApiHttpError && error.status === 404) && count < 2,
  });

  const data = query.data;
  const firstModel = data?.attempts.find((a) => a.model !== null)?.model ?? null;
  const documentCount = data !== undefined ? requestDocumentCount(data.files) : 0;
  const activeTab: DetailTab = tab === 'documents' && documentCount === 0 ? 'input' : tab;
  const margin = data !== undefined ? marginStat(data) : null;
  const attemptCount = data?.attempts.length ?? 0;

  return (
    <>
      <PageChrome
        crumbs={[
          { label: 'Requêtes', to: '/requests' },
          { label: shortRequestId(requestId), mono: true },
        ]}
      />
      <PageHead
        title="Détail de la requête"
        subtitle={
          data !== undefined
            ? `${data.client?.name ?? 'Client inconnu'}${data.useCase !== null ? `, cas d'usage ${data.useCase}` : ''}`
            : undefined
        }
        tools={
          <>
            <span className="font-mono text-xs text-muted-foreground">{requestId}</span>
            <CopyButton text={requestId} label="Copier l'identifiant" />
            {data !== undefined ? <StatusBadge status={requestStatusLabel(data.status)} /> : null}
          </>
        }
      />

      {query.isLoading ? (
        <Card>
          <LoadingState />
        </Card>
      ) : null}
      {query.isError ? (
        <Callout variant="destructive">
          {query.error instanceof ApiHttpError && query.error.status === 404
            ? 'Requête introuvable.'
            : 'Impossible de charger la requête.'}
        </Callout>
      ) : null}

      {data !== undefined && margin !== null ? (
        <>
          <Row4 aria-label="Économie de la requête">
            <StatCard
              label="Montant facturé"
              value={formatEur(data.amount)}
              foot={data.billable ? 'HT, facturable' : 'HT, non facturable'}
            />
            <StatCard
              label="Coût fournisseur"
              value={formatEur(data.providerCost)}
              foot={providerCostFoot(data)}
            />
            <StatCard label="Marge" value={margin.value} foot={margin.foot} />
            <StatCard
              label="Durée"
              value={
                data.finishedAt !== null ? formatDuration(data.createdAt, data.finishedAt) : '—'
              }
              foot={
                data.finishedAt !== null
                  ? `${formatInt(attemptCount)} tentative${attemptCount > 1 ? 's' : ''}`
                  : 'Non terminée'
              }
            />
          </Row4>

          <Split21 className="items-start">
            <Card>
              <Tabs
                value={activeTab}
                onValueChange={(next) => {
                  if (
                    next === 'input' ||
                    next === 'attempts' ||
                    next === 'result' ||
                    next === 'documents'
                  ) {
                    setTab(next);
                  }
                }}
              >
                <CardBar
                  start={
                    <TabsList aria-label="Contenu de la requête">
                      <TabsTrigger value="input">Requête entrante</TabsTrigger>
                      <TabsTrigger value="attempts">
                        Tentatives <TabCount>{formatInt(attemptCount)}</TabCount>
                      </TabsTrigger>
                      <TabsTrigger value="result">Réponse</TabsTrigger>
                      {documentCount > 0 ? (
                        <TabsTrigger value="documents">
                          Documents <TabCount>{formatInt(documentCount)}</TabCount>
                        </TabsTrigger>
                      ) : null}
                    </TabsList>
                  }
                />

                <TabsContent value="input" className="mt-0">
                  <CardContent className="grid gap-4">
                    {data.contentPurged ? (
                      <PurgeNotice />
                    ) : data.input === null ? (
                      <EmptyState title="Aucune entrée enregistrée" />
                    ) : (
                      <>
                        <RequestInputView input={data.input} />
                        <Details title="Entrée brute (normalisée)">
                          <JsonBlock value={data.input} />
                        </Details>
                      </>
                    )}
                  </CardContent>
                </TabsContent>

                <TabsContent value="attempts" className="mt-0">
                  <CardContent className="grid gap-4">
                    {data.attempts.length === 0 ? (
                      <EmptyState title="Aucune tentative" description="Requête non exécutée." />
                    ) : (
                      data.attempts.map((attempt) => (
                        <AttemptPanel
                          key={attempt.number}
                          attempt={attempt}
                          contentPurged={data.contentPurged}
                        />
                      ))
                    )}
                  </CardContent>
                </TabsContent>

                <TabsContent value="result" className="mt-0">
                  <CardContent>
                    {data.contentPurged ? (
                      <PurgeNotice />
                    ) : data.result === null ? (
                      <EmptyState title="Aucun résultat" description="Requête non aboutie." />
                    ) : (
                      <ResultView result={data.result} />
                    )}
                  </CardContent>
                </TabsContent>

                {documentCount > 0 ? (
                  <TabsContent value="documents" className="mt-0">
                    <CardContent>
                      <RequestDocuments files={data.files} />
                    </CardContent>
                  </TabsContent>
                ) : null}
              </Tabs>
            </Card>

            <Stack>
              <Card>
                <CardHead title="Résumé" />
                <CardContent className="pt-0">
                  <KvList>
                    <KvRow label="Client">{data.client?.name ?? '—'}</KvRow>
                    <KvRow label="Référence" mono>
                      <span className="break-all text-xs">{data.reference ?? '—'}</span>
                    </KvRow>
                    <KvRow label="Cas d'usage" mono>
                      {data.useCase ?? '—'}
                    </KvRow>
                    <KvRow label="Modèle" mono={firstModel !== null}>
                      {firstModel === null
                        ? '—'
                        : `${firstModel.provider}, ${firstModel.providerModelId}`}
                    </KvRow>
                    <KvRow label="Environnement">
                      <EnvironmentBadge environment={data.environment} />
                    </KvRow>
                    <KvRow label="Facturable">{data.billable ? 'Oui' : 'Non'}</KvRow>
                    <KvRow label="Issue">
                      {data.outcome === null ? (
                        '—'
                      ) : (
                        <span className="grid justify-items-end gap-0.5">
                          <span>{outcomeLabel(data.outcome.code)}</span>
                          <span className="font-mono text-xs text-muted-foreground">
                            {data.outcome.code}
                          </span>
                        </span>
                      )}
                    </KvRow>
                    {data.useCaseKind === 'sms' ? (
                      // Counted at submission; a non-billable outcome bills none of them.
                      <KvRow label={smsUnitsLabel(data.outcome)} mono>
                        {formatInt(data.units)}
                      </KvRow>
                    ) : null}
                    <KvRow label="Tentatives" mono>
                      {formatInt(attemptCount)}
                    </KvRow>
                  </KvList>
                  {data.outcome !== null && data.outcome.message !== null ? (
                    <p className="m-0 mt-2 text-caption text-muted-foreground">
                      {data.outcome.message}
                    </p>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHead title="Chronologie" />
                <CardContent className="pt-0">
                  <Timeline
                    entries={[
                      { key: 'created', label: 'Créée', time: formatTimeSeconds(data.createdAt) },
                      {
                        key: 'started',
                        label: 'Démarrée',
                        time: timelineTime(data.startedAt, data.createdAt),
                      },
                      {
                        key: 'finished',
                        label: 'Terminée',
                        time: timelineTime(data.finishedAt, data.createdAt),
                      },
                    ]}
                  />
                  <CardDescription className="mt-2">
                    {formatLongDate(data.createdAt)}
                  </CardDescription>
                </CardContent>
              </Card>

              {Object.keys(data.metadata).length > 0 ? (
                <Card>
                  <CardHead title="Métadonnées" />
                  <CardContent className="pt-0">
                    <JsonBlock value={data.metadata} />
                  </CardContent>
                </Card>
              ) : null}
            </Stack>
          </Split21>
        </>
      ) : null}
    </>
  );
}
